import { DeleteOutlined, EditOutlined, ReloadOutlined } from "@ant-design/icons";
import { Alert, App, Button, Space, Tag } from "antd";
import { useQuery } from "@tanstack/react-query";
import type { ColumnsType } from "antd/es/table";
import { deleteDraft, getDrafts, type EntryDraftModuleKey, type EntryDraftRecord } from "../../api/draftApi";
import { DataGrid } from "../components/DataGrid";
import { formatDate } from "../utils/date";
import { getApiErrorMessage } from "../utils/apiError";

export function EntryDraftList({
  moduleKey,
  active,
  onOpen
}: {
  moduleKey: EntryDraftModuleKey;
  active: boolean;
  onOpen: (draft: EntryDraftRecord) => void;
}) {
  const { message, modal } = App.useApp();
  const query = useQuery({
    queryKey: ["entry-drafts", moduleKey],
    enabled: active,
    queryFn: () => getDrafts(moduleKey)
  });

  const confirmDeleteDraft = (row: EntryDraftRecord) => {
    modal.confirm({
      title: "Delete draft?",
      content: `This will permanently remove the draft "${row.title}".`,
      okText: "Delete Draft",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await deleteDraft(row.id);
          message.success("Draft deleted");
          await query.refetch();
        } catch (err) {
          message.error(getApiErrorMessage(err, "Unable to delete draft"));
        }
      }
    });
  };

  const columns: ColumnsType<EntryDraftRecord> = [
    { title: "Draft", dataIndex: "title", width: 340 },
    { title: "Saved", dataIndex: "updatedAt", width: 180, render: formatDate },
    { title: "Module", dataIndex: "moduleKey", width: 150, render: (value) => <Tag>{value}</Tag> },
    {
      key: "actions",
      title: "Actions",
      width: 220,
      fixed: "right",
      render: (_, row) => (
        <Space size={4}>
          <Button size="small" icon={<EditOutlined />} onClick={() => onOpen(row)}>Edit Draft</Button>
          <Button
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => confirmDeleteDraft(row)}
          >
            Delete Draft
          </Button>
        </Space>
      )
    }
  ];

  return (
    <div>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="How to use drafts"
        description="Double-click a draft row or click the edit button to load it into the entry form. Review the values there, then use Post or Save & Generate PDF to complete it."
      />
      <div className="draft-list-toolbar">
        <Button icon={<ReloadOutlined />} onClick={() => query.refetch()}>Refresh Drafts</Button>
      </div>
      <DataGrid<EntryDraftRecord>
        rows={query.data ?? []}
        columns={columns}
        loading={query.isLoading || query.isFetching}
        tableId={`entry-drafts-${moduleKey}`}
        pagination={false}
        onRowDoubleClick={onOpen}
      />
      {query.error ? <div className="inline-error">Unable to load drafts.</div> : null}
    </div>
  );
}
